import { Button, IconButton, Modal, Switch } from '@affine/component';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { CloseIcon } from '@blocksuite/icons/rc';
import { useService } from '@toeverything/infra';
import React, { useCallback, useEffect, useState } from 'react';

import {
  backupAllRecordingsToDrive,
  backupDocsToDrive,
  getGoogleDriveConfig,
  type GoogleDriveConfig,
  initiateGISSignIn,
  saveGoogleDriveConfig,
  verifyGoogleDriveToken,
} from './google-drive-service';

export interface GoogleDriveModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const GoogleDriveModal: React.FC<GoogleDriveModalProps> = ({
  open,
  onOpenChange,
}) => {
  const currentWorkspace = useService(WorkspaceService).workspace;
  const [config, setConfig] = useState<GoogleDriveConfig>(getGoogleDriveConfig());
  const [tokenInput, setTokenInput] = useState('');
  const [clientIdInput, setClientIdInput] = useState(config.clientId || '');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [progressStatus, setProgressStatus] = useState<string>('');
  const [progressPercent, setProgressPercent] = useState<number>(0);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  useEffect(() => {
    if (open) {
      const cfg = getGoogleDriveConfig();
      setConfig(cfg);
      setClientIdInput(cfg.clientId || '');
      setErrorMessage('');
      setSuccessMessage('');
      setProgressStatus('');

      if (cfg.accessToken && !cfg.userEmail) {
        setIsVerifying(true);
        verifyGoogleDriveToken(cfg.accessToken)
          .then(res => {
            if (res.valid && res.user) {
              const updated = saveGoogleDriveConfig({
                userEmail: res.user.email,
                userName: res.user.name,
              });
              setConfig(updated);
            }
          })
          .catch(console.error)
          .finally(() => setIsVerifying(false));
      }
    }
  }, [open]);

  // Handle Token Connect
  const handleConnectToken = useCallback(async () => {
    if (!tokenInput.trim()) {
      setErrorMessage('Vui lòng nhập Access Token từ Google');
      return;
    }
    setErrorMessage('');
    setSuccessMessage('');
    setIsVerifying(true);

    try {
      const res = await verifyGoogleDriveToken(tokenInput.trim());
      if (res.valid && res.user) {
        const updated = saveGoogleDriveConfig({
          accessToken: tokenInput.trim(),
          userEmail: res.user.email,
          userName: res.user.name,
        });
        setConfig(updated);
        setTokenInput('');
        setSuccessMessage(`Đã kết nối thành công với tài khoản: ${res.user.email}`);
      } else {
        setErrorMessage(res.error || 'Token không hợp lệ hoặc đã hết hạn');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Lỗi kiểm tra token');
    } finally {
      setIsVerifying(false);
    }
  }, [tokenInput]);

  // Handle Google Identity Services (GIS) Sign In
  const handleGISSignIn = useCallback(async () => {
    if (!clientIdInput.trim()) {
      setErrorMessage('Vui lòng nhập Google OAuth Client ID');
      return;
    }
    setErrorMessage('');
    setSuccessMessage('');
    setIsVerifying(true);

    try {
      const token = await initiateGISSignIn(clientIdInput.trim());
      const verifyRes = await verifyGoogleDriveToken(token);
      const updated = saveGoogleDriveConfig({
        accessToken: token,
        clientId: clientIdInput.trim(),
        userEmail: verifyRes.user?.email || 'Google User',
        userName: verifyRes.user?.name || 'Google User',
      });
      setConfig(updated);
      setSuccessMessage('Đăng nhập Google Drive thành công!');
    } catch (err: any) {
      setErrorMessage(err.message || 'Đăng nhập Google thất bại');
    } finally {
      setIsVerifying(false);
    }
  }, [clientIdInput]);

  // Handle Disconnect
  const handleDisconnect = useCallback(() => {
    const updated = saveGoogleDriveConfig({
      accessToken: '',
      userEmail: '',
      userName: '',
      lastSyncTime: undefined,
    });
    setConfig(updated);
    setSuccessMessage('Đã ngắt kết nối Google Drive.');
  }, []);

  // Handle Toggle Auto Backup
  const handleToggleAutoBackup = useCallback(
    (checked: boolean) => {
      const updated = saveGoogleDriveConfig({ autoBackup: checked });
      setConfig(updated);
    },
    []
  );

  // Backup Recordings
  const handleBackupRecordings = useCallback(async () => {
    setIsBackingUp(true);
    setErrorMessage('');
    setSuccessMessage('');
    setProgressPercent(10);

    const result = await backupAllRecordingsToDrive((status, curr, total) => {
      setProgressStatus(status);
      if (total > 0) {
        setProgressPercent(Math.min(95, Math.round((curr / total) * 100)));
      }
    });

    setIsBackingUp(false);
    if (result.success) {
      setProgressPercent(100);
      setSuccessMessage(`Đã sao lưu thành công ${result.count} bài giảng & bản ghi chú!`);
      setConfig(getGoogleDriveConfig());
    } else {
      setErrorMessage(result.error || 'Sao lưu bài giảng thất bại');
    }
  }, []);

  // Backup Workspace Docs
  const handleBackupDocs = useCallback(async () => {
    setIsBackingUp(true);
    setErrorMessage('');
    setSuccessMessage('');
    setProgressPercent(10);

    try {
      const docsToBackup: Array<{ id: string; title: string; content?: string }> = [];
      const docRecordList = currentWorkspace.docCollection.docs;

      docRecordList.forEach(doc => {
        const title = doc.meta?.title || 'Tài liệu không tên';
        let markdown = `# ${title}\n\n`;
        try {
          const notes = doc.getBlocksByFlavour('affine:note');
          if (notes && notes.length > 0) {
            notes.forEach(() => {
              const paragraphs = doc.getBlocksByFlavour('affine:paragraph');
              paragraphs.forEach(p => {
                if (p.model.text) {
                  markdown += `${p.model.text.toString()}\n\n`;
                }
              });
            });
          }
        } catch {}
        docsToBackup.push({ id: doc.id, title, content: markdown });
      });

      const result = await backupDocsToDrive(docsToBackup, (status, curr, total) => {
        setProgressStatus(status);
        if (total > 0) {
          setProgressPercent(Math.min(95, Math.round((curr / total) * 100)));
        }
      });

      setIsBackingUp(false);
      if (result.success) {
        setProgressPercent(100);
        setSuccessMessage(`Đã sao lưu thành công ${result.count} tài liệu lên Google Drive!`);
        setConfig(getGoogleDriveConfig());
      } else {
        setErrorMessage(result.error || 'Sao lưu tài liệu thất bại');
      }
    } catch (err: any) {
      setIsBackingUp(false);
      setErrorMessage(err.message || 'Lỗi khi chuẩn bị tài liệu sao lưu');
    }
  }, [currentWorkspace]);

  const isConnected = !!config.accessToken && !!config.userEmail;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      width={560}
      contentOptions={{
        style: {
          padding: 0,
          borderRadius: '16px',
          overflow: 'hidden',
          background: 'var(--affine-background-primary-color, #ffffff)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.18)',
        },
      }}
    >
      <div style={{ padding: '24px 28px' }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '18px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'rgba(66, 133, 244, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* Google Drive SVG Icon */}
              <svg width="24" height="24" viewBox="0 0 87.3 78" fill="none">
                <path
                  d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5z"
                  fill="#0066DA"
                />
                <path
                  d="M43.65 25 29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44A8.9 8.9 0 0 0 0 53h27.5z"
                  fill="#00AC47"
                />
                <path
                  d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.8l5.85 10.1z"
                  fill="#EA4335"
                />
                <path
                  d="M43.65 25 57.4 1.2C56.05.4 54.5 0 52.95 0H34.35c-1.55 0-3.1.4-4.45 1.2z"
                  fill="#00832D"
                />
                <path
                  d="M59.8 53H27.5L13.75 76.8c1.35.8 2.9 1.2 4.45 1.2h50.9c1.55 0 3.1-.4 4.45-1.2z"
                  fill="#2684FC"
                />
                <path
                  d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.15 28h27.5c0-1.55-.4-3.1-1.2-4.5z"
                  fill="#FFBA00"
                />
              </svg>
            </div>
            <div>
              <h2
                style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  margin: 0,
                  color: 'var(--affine-text-primary-color, #1e293b)',
                }}
              >
                Google Drive Sync & Backup
              </h2>
              <p
                style={{
                  fontSize: '12.5px',
                  margin: '2px 0 0 0',
                  color: 'var(--affine-text-secondary-color, #64748b)',
                }}
              >
                Đồng bộ bài giảng & tài liệu Cáo Ơi vào Google Drive
              </p>
            </div>
          </div>
          <IconButton
            size="small"
            onClick={() => onOpenChange(false)}
            style={{ borderRadius: '8px' }}
          >
            <CloseIcon />
          </IconButton>
        </div>

        {/* Status Alerts */}
        {errorMessage && (
          <div
            style={{
              padding: '10px 14px',
              borderRadius: '8px',
              background: '#fef2f2',
              color: '#dc2626',
              fontSize: '13px',
              marginBottom: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span>⚠️</span>
            <span>{errorMessage}</span>
          </div>
        )}

        {successMessage && (
          <div
            style={{
              padding: '10px 14px',
              borderRadius: '8px',
              background: '#f0fdf4',
              color: '#16a34a',
              fontSize: '13px',
              marginBottom: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span>✅</span>
            <span>{successMessage}</span>
          </div>
        )}

        {/* Connection State Card */}
        <div
          style={{
            background: 'var(--affine-background-secondary-color, #f8fafc)',
            border: '1px solid var(--affine-border-color, #e2e8f0)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '18px',
          }}
        >
          {isConnected ? (
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div
                    style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      background: '#22c55e',
                    }}
                  />
                  <div>
                    <div
                      style={{
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--affine-text-primary-color, #0f172a)',
                      }}
                    >
                      {config.userName || 'Tài khoản Google Drive'}
                    </div>
                    <div
                      style={{
                        fontSize: '12px',
                        color: 'var(--affine-text-secondary-color, #64748b)',
                      }}
                    >
                      {config.userEmail}
                    </div>
                  </div>
                </div>
                <Button
                  size="small"
                  variant="secondary"
                  onClick={handleDisconnect}
                  style={{ fontSize: '12px' }}
                >
                  Ngắt kết nối
                </Button>
              </div>

              <div
                style={{
                  marginTop: '12px',
                  paddingTop: '12px',
                  borderTop: '1px solid var(--affine-border-color, #e2e8f0)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  color: 'var(--affine-text-secondary-color, #64748b)',
                }}
              >
                <span>Thư mục: 📁 AFFiNE - Cáo Ơi Backups</span>
                <span>
                  {config.lastSyncTime
                    ? `Lần cuối: ${new Date(config.lastSyncTime).toLocaleTimeString('vi-VN', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })} (${new Date(config.lastSyncTime).toLocaleDateString('vi-VN')})`
                    : 'Chưa sao lưu'}
                </span>
              </div>
            </div>
          ) : (
            <div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px',
                }}
              >
                <div
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: '#94a3b8',
                  }}
                />
                <span
                  style={{
                    fontSize: '13.5px',
                    fontWeight: 500,
                    color: 'var(--affine-text-primary-color, #334155)',
                  }}
                >
                  Chưa kết nối Google Drive
                </span>
              </div>

              {/* Quick Connect with Access Token */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <input
                  type="password"
                  placeholder="Dán Google Drive Access Token (hoặc Bearer token)..."
                  value={tokenInput}
                  onChange={e => setTokenInput(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--affine-border-color, #cbd5e1)',
                    background: 'var(--affine-background-primary-color, #ffffff)',
                    fontSize: '12.5px',
                    outline: 'none',
                  }}
                />
                <Button
                  onClick={() => {
                    handleConnectToken().catch(console.error);
                  }}
                  disabled={isVerifying || !tokenInput.trim()}
                  style={{
                    background: '#2563eb',
                    color: '#ffffff',
                    fontWeight: 500,
                    fontSize: '13px',
                  }}
                >
                  {isVerifying ? 'Đang kiểm tra...' : 'Kết nối'}
                </Button>
              </div>

              {/* OAuth GIS Toggle */}
              <div style={{ marginTop: '8px' }}>
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    fontSize: '12px',
                    color: '#2563eb',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  {showAdvanced
                    ? '▲ Ẩn cấu hình Google OAuth Client ID'
                    : '▼ Hoặc đăng nhập bằng Google OAuth Client ID (Một chạm)'}
                </button>

                {showAdvanced && (
                  <div
                    style={{
                      marginTop: '10px',
                      padding: '10px',
                      background: 'rgba(255,255,255,0.7)',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <p style={{ fontSize: '11.5px', color: '#64748b', margin: '0 0 6px 0' }}>
                      Nhập Google OAuth Web Client ID (từ Google Cloud Console) để bật popup đăng nhập Google một chạm:
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        placeholder="apps.googleusercontent.com Client ID..."
                        value={clientIdInput}
                        onChange={e => setClientIdInput(e.target.value)}
                        style={{
                          flex: 1,
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid #cbd5e1',
                          fontSize: '12px',
                        }}
                      />
                      <Button
                        size="small"
                        onClick={() => {
                          handleGISSignIn().catch(console.error);
                        }}
                        disabled={isVerifying || !clientIdInput.trim()}
                        style={{ background: '#4285F4', color: '#fff' }}
                      >
                        Đăng nhập Google
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Backup Actions */}
        <div style={{ marginBottom: '18px' }}>
          <h3
            style={{
              fontSize: '13px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: 'var(--affine-text-secondary-color, #64748b)',
              marginBottom: '10px',
            }}
          >
            Tác vụ sao lưu
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div
              style={{
                border: '1px solid var(--affine-border-color, #e2e8f0)',
                borderRadius: '10px',
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>
                  🎙️ Bài giảng & Âm thanh
                </div>
                <div style={{ fontSize: '11.5px', color: '#64748b', marginBottom: '12px' }}>
                  Tải toàn bộ file ghi âm (.webm) và bản dịch Whisper lên thư mục Recordings.
                </div>
              </div>
              <Button
                onClick={() => {
                  handleBackupRecordings().catch(console.error);
                }}
                disabled={!isConnected || isBackingUp}
                style={{
                  width: '100%',
                  background: isConnected ? '#ef4444' : '#cbd5e1',
                  color: '#ffffff',
                  fontWeight: 500,
                  fontSize: '12.5px',
                }}
              >
                {isBackingUp ? 'Đang tải lên...' : 'Sao lưu bài giảng ngay'}
              </Button>
            </div>

            <div
              style={{
                border: '1px solid var(--affine-border-color, #e2e8f0)',
                borderRadius: '10px',
                padding: '12px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>
                  📄 Ghi chú & Tài liệu
                </div>
                <div style={{ fontSize: '11.5px', color: '#64748b', marginBottom: '12px' }}>
                  Xuất tất cả trang ghi chú dưới dạng Markdown (.md) lên thư mục Documents.
                </div>
              </div>
              <Button
                onClick={() => {
                  handleBackupDocs().catch(console.error);
                }}
                disabled={!isConnected || isBackingUp}
                style={{
                  width: '100%',
                  background: isConnected ? '#3b82f6' : '#cbd5e1',
                  color: '#ffffff',
                  fontWeight: 500,
                  fontSize: '12.5px',
                }}
              >
                {isBackingUp ? 'Đang tải lên...' : 'Sao lưu tài liệu ngay'}
              </Button>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        {isBackingUp && (
          <div style={{ marginBottom: '16px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '12px',
                color: '#64748b',
                marginBottom: '4px',
              }}
            >
              <span>{progressStatus || 'Đang sao lưu...'}</span>
              <span>{progressPercent}%</span>
            </div>
            <div
              style={{
                height: '6px',
                background: '#e2e8f0',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${progressPercent}%`,
                  height: '100%',
                  background: '#2563eb',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </div>
        )}

        {/* Auto Backup Toggle */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 14px',
            background: 'var(--affine-background-secondary-color, #f8fafc)',
            borderRadius: '10px',
            border: '1px solid var(--affine-border-color, #e2e8f0)',
            marginBottom: '16px',
          }}
        >
          <div>
            <div style={{ fontSize: '13.5px', fontWeight: 600 }}>
              ⚡ Tự động sao lưu bài giảng mới
            </div>
            <div style={{ fontSize: '11.5px', color: '#64748b' }}>
              Mỗi khi kết thúc ghi âm bài giảng, tự động đồng bộ ngay lên Google Drive.
            </div>
          </div>
          <Switch
            checked={config.autoBackup}
            onChange={handleToggleAutoBackup}
            disabled={!isConnected}
          />
        </div>

        {/* Desktop / Safari / Ark Tips */}
        <div
          style={{
            fontSize: '11.5px',
            color: '#64748b',
            background: 'rgba(59, 130, 246, 0.05)',
            padding: '10px 14px',
            borderRadius: '8px',
            border: '1px solid rgba(59, 130, 246, 0.15)',
          }}
        >
          💡 <strong>Mẹo dành cho Ark & Safari trên macOS:</strong> Khi mẹ hoặc bạn sử dụng Google Drive for Desktop, các tệp âm thanh & tài liệu được sao lưu tại thư mục <code>AFFiNE - Cáo Ơi Backups</code> sẽ lập tức xuất hiện ngay trong Finder của máy Mac.
        </div>
      </div>
    </Modal>
  );
};
